import {
  Paper,
  Stack,
  Title,
  Group,
  Text,
  Divider,
  LoadingOverlay,
  Badge,
  Button,
  Box,
} from '@mantine/core';
import { IconCheck, IconX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useConsent } from '@hooks/useConsent';
import { useBlobUrls } from '@utils/blob.utils';
import { AttachmentDisplay } from '@medplum-components/AttachmentDisplay/AttachmentDisplay';
import { AttachmentDisplayContext } from '@medplum-components/AttachmentDisplay/AttachmentDisplayContext';
import { formatHumanName } from '@medplum/core';
import { HumanName } from '@medplum/fhirtypes';
import { getTranslatedRelationshipDisplay } from '@/utils/consent.utils';
import { DateDisplayFormatter, useDateDisplay } from '@hooks/useDateDisplay';
import { useConceptMap } from '../hooks/useConceptMap';

interface ConsentDisplayProps {
  serviceRequest: import('@medplum/fhirtypes').ServiceRequest;
  onEdit?: () => void;
}

interface ConsentItemProps {
  label: string;
  value: boolean | null;
  date: Date | null;
  accepted: string;
  declined: string;
  formatDate: DateDisplayFormatter;
}

// Define ConsentItem component outside parent scope
const ConsentItem = ({
  label,
  value,
  date,
  accepted,
  declined,
  formatDate,
}: ConsentItemProps) => (
  <Group justify="space-between" p="sm" bg="gray.0">
    <Group>
      {value ? (
        <IconCheck size={20} color="green" />
      ) : (
        <IconX size={20} color="red" />
      )}
      <Text size="sm">{label}</Text>
    </Group>
    <Group>
      <Badge color={value ? 'green' : 'red'} variant="light">
        {value ? accepted : declined}
      </Badge>
      {date && (
        <Text size="sm" c="dimmed">
          {formatDate(date)}
        </Text>
      )}
    </Group>
  </Group>
);

export function ConsentDisplay({
  serviceRequest,
  onEdit,
}: ConsentDisplayProps) {
  const { t } = useTranslation(['lims', 'common']);
  const { loading, consent, consentData, documents } =
    useConsent(serviceRequest);
  const { isConsentRequired } = useConceptMap();
  const { createBlobUrl } = useBlobUrls();
  const formatDate = useDateDisplay();
  const formatDateTime = useDateDisplay(
    {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    },
    ''
  );

  if (loading) {
    return (
      <Paper shadow="sm" p="md" withBorder pos="relative">
        <LoadingOverlay visible />
      </Paper>
    );
  }

  if (!consent || !consentData) {
    return null;
  }

  // Determine if general consent is denied
  const isGeneralConsentDenied = consentData.general === false;
  const isGeneralConsentAccepted = consentData.general === true;

  return (
    <Paper shadow="sm" p="md" withBorder>
      <Stack gap="md">
        <Group justify="space-between">
          <Group>
            <Title order={3}>
              {t('consent.display.title', { ns: 'lims' })}
            </Title>
            <Badge
              color={isGeneralConsentDenied ? 'red' : 'green'}
              size="lg"
              variant="light"
            >
              <Group gap={4}>
                {isGeneralConsentDenied ? (
                  <IconX size={14} />
                ) : (
                  <IconCheck size={14} />
                )}
                {isGeneralConsentDenied
                  ? t('consent.display.denied', { ns: 'lims' })
                  : t('consent.display.completed', { ns: 'lims' })}
              </Group>
            </Badge>
          </Group>
          {onEdit && (
            <Button variant="subtle" size="sm" onClick={onEdit}>
              {t('consent.display.editButton', { ns: 'lims' })}
            </Button>
          )}
        </Group>

        <Divider />

        {/* Signer Information */}
        {consentData.signerType && (
          <Group gap="sm">
            <Text fw={600}>
              {t('consent.display.signerInfo', { ns: 'lims' })}
            </Text>
            <Group justify="space-between" p="sm" bg="gray.0" flex={1}>
              <Text size="sm">
                {consentData.signerType === 'patient'
                  ? t('consent.signer.patient', { ns: 'lims' })
                  : formatHumanName(
                      consentData.selectedRelatedPerson?.name?.[0] as HumanName
                    ) ||
                    consentData.newRelatedPersonName ||
                    t('consent.signer.unknown', { ns: 'lims' })}
              </Text>
              <Badge variant="light">
                {consentData.signerType === 'patient'
                  ? t('consent.signer.patient', { ns: 'lims' })
                  : (() => {
                      const relationshipDisplay =
                        consentData.selectedRelatedPerson?.relationship?.[0]
                          ?.coding?.[0]?.display ||
                        consentData.newRelatedPersonRelationship;

                      if (relationshipDisplay) {
                        return getTranslatedRelationshipDisplay(
                          relationshipDisplay,
                          t
                        );
                      }

                      return t('consent.signer.other', { ns: 'lims' });
                    })()}
              </Badge>
            </Group>
          </Group>
        )}

        <Divider />

        <Stack gap="sm">
          {/* General Consent - Always shown at the top */}
          <ConsentItem
            label={t('consent.sections.general.title', { ns: 'lims' })}
            value={consentData.general}
            date={consentData.generalDate}
            accepted={t('consent.display.accepted', { ns: 'lims' })}
            declined={t('consent.display.declined', { ns: 'lims' })}
            formatDate={formatDate}
          />

          {/* Other consents - only show if general consent is accepted */}
          {isGeneralConsentAccepted && (
            <Stack gap="sm" ml="xl">
              <ConsentItem
                label={t('consent.sections.scientificUse.title', {
                  ns: 'lims',
                })}
                value={consentData.scientificUse}
                date={consentData.scientificUseDate}
                accepted={t('consent.display.accepted', { ns: 'lims' })}
                declined={t('consent.display.declined', { ns: 'lims' })}
                formatDate={formatDate}
              />

              {consentData.scientificUse &&
                isConsentRequired(serviceRequest, 'photo-video') && (
                  <Box ml="xl">
                    <ConsentItem
                      label={t('consent.sections.photoVideo.checkbox', {
                        ns: 'lims',
                      })}
                      value={consentData.photoVideoUse}
                      date={consentData.photoVideoUseDate || null}
                      accepted={t('consent.display.accepted', { ns: 'lims' })}
                      declined={t('consent.display.declined', { ns: 'lims' })}
                      formatDate={formatDate}
                    />
                  </Box>
                )}

              {isConsentRequired(serviceRequest, 'incidental-findings') && (
                <ConsentItem
                  label={t('consent.sections.incidentalFindings.title', {
                    ns: 'lims',
                  })}
                  value={consentData.incidentalFindings}
                  date={consentData.incidentalFindingsDate}
                  accepted={t('consent.display.accepted', { ns: 'lims' })}
                  declined={t('consent.display.declined', { ns: 'lims' })}
                  formatDate={formatDate}
                />
              )}

              {isConsentRequired(serviceRequest, 'secondary-findings') && (
                <ConsentItem
                  label={t('consent.sections.secondaryFindings.title', {
                    ns: 'lims',
                  })}
                  value={consentData.secondaryFindings}
                  date={consentData.secondaryFindingsDate}
                  accepted={t('consent.display.accepted', { ns: 'lims' })}
                  declined={t('consent.display.declined', { ns: 'lims' })}
                  formatDate={formatDate}
                />
              )}
            </Stack>
          )}
        </Stack>

        {documents.length > 0 && (
          <>
            <Divider />
            <Stack gap="sm">
              <Text fw={600}>
                {t('consent.display.attachedDocuments', { ns: 'lims' })}
              </Text>
              <AttachmentDisplayContext.Provider value={{ preview: false }}>
                {documents.map((doc, index) => {
                  const attachment = doc.content?.[0]?.attachment;
                  if (!attachment) return null;

                  // Convert base64 data to URL if needed
                  const attachmentWithUrl =
                    attachment.data && !attachment.url
                      ? {
                          ...attachment,
                          url: createBlobUrl(
                            attachment.data,
                            attachment.contentType || 'application/octet-stream'
                          ),
                        }
                      : attachment;

                  return (
                    <Group
                      key={index}
                      p="sm"
                      bd="1px solid #e0e0e0"
                      bg="gray.0"
                    >
                      <AttachmentDisplay value={attachmentWithUrl} />
                      {doc.date && (
                        <Text size="xs" c="dimmed">
                          {formatDate(doc.date)}
                        </Text>
                      )}
                    </Group>
                  );
                })}
              </AttachmentDisplayContext.Provider>
            </Stack>
          </>
        )}

        {consent?.meta?.lastUpdated && (
          <Text size="xs" c="dimmed" ta="right">
            {t('consent.display.lastUpdated', { ns: 'lims' })}:{' '}
            {formatDateTime(consent.meta.lastUpdated)}
          </Text>
        )}
      </Stack>
    </Paper>
  );
}
